import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import {
  listMedia,
  deleteMedia,
  type MediaItem,
  type MediaScope,
} from "@/services/mediaService";
import { uploadImage } from "@/services/imageUploadService";

/**
 * Shared Media Handler block.
 *
 * ONE library UI used by BOTH the admin console and the customer (owner) portal.
 * It shows the files already stored on the server, lets you search / filter by
 * folder, upload new images and — depending on the props — either manage the
 * library (preview, copy, delete) or pick from it.
 *
 * `mode="pick"`   → selection grid + “Use selected” bar (used by the picker).
 * `mode="manage"` → full library with preview, copy, open and (admin) delete.
 */

/** Folder filter options shown in every library instance. */
export const MEDIA_FOLDERS = [
  { value: "", label: "All folders" },
  { value: "listings", label: "Listings" },
  { value: "gallery", label: "Gallery" },
  { value: "ads", label: "Ads" },
  { value: "logos", label: "Logos" },
  { value: "avatars", label: "Avatars" },
  { value: "media", label: "Media" },
  { value: "branding", label: "Branding" },
  { value: "fonts", label: "Fonts" },
];

type UploadFolder = "listings" | "gallery" | "ads" | "logos" | "avatars" | "media" | "branding";

interface MediaLibraryProps {
  /** Bearer token for the authenticated media API. */
  token: string | null;
  /** Which side owns the library — decides the endpoint. */
  scope?: MediaScope;
  /** "manage" = full library · "pick" = choose an image. */
  mode?: "manage" | "pick";
  /** Pick mode: allow selecting several images. */
  multiple?: boolean;
  /** Pick mode: hard cap when multiple is true. */
  maxSelect?: number;
  /** Pick mode: URLs already in use (highlighted, pre-selected). */
  currentUrls?: string[];
  /** Pick mode: called with the chosen URL(s) on confirm. */
  onSelect?: (urls: string[]) => void;
  /** Pick mode: called when the user cancels. */
  onCancel?: () => void;
  /** Show the upload controls (defaults to true in manage mode). */
  canUpload?: boolean;
  /** Show the delete action (defaults to true only for admins). */
  canDelete?: boolean;
  /** Destination folder for new uploads. */
  uploadFolder?: UploadFolder;
  /** Fired whenever the file list is (re)loaded — used for storage stats. */
  onDataChange?: (items: MediaItem[]) => void;
}

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

export default function MediaLibrary({
  token,
  scope = "admin",
  mode = "manage",
  multiple = false,
  maxSelect = 8,
  currentUrls,
  onSelect,
  onCancel,
  canUpload,
  canDelete,
  uploadFolder = "media",
  onDataChange,
}: MediaLibraryProps) {
  const allowUpload = canUpload ?? mode === "manage";
  const allowDelete = canDelete ?? scope === "admin";
  const pickMode = mode === "pick";

  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState("");
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState<string[]>(currentUrls ?? []);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MediaItem | null>(null);
  const [busyName, setBusyName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  const dataChangeRef = useRef(onDataChange);
  useEffect(() => {
    dataChangeRef.current = onDataChange;
  }, [onDataChange]);

  // Pre-select the images already in use (stable key avoids render loops).
  const currentKey = (currentUrls ?? []).join("|");
  useEffect(() => {
    setSelected(currentUrls ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    listMedia(token, scope)
      .then((data) => {
        if (!active) return;
        setItems(data);
        dataChangeRef.current?.(data);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "We could not load the media library.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, scope, reloadKey]);

  function flash(text: string) {
    setMessage(text);
    window.setTimeout(() => setMessage(""), 3000);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (folder && item.folder !== folder) return false;
      if (q && !item.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, folder]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) map.set(item.folder, (map.get(item.folder) ?? 0) + 1);
    return map;
  }, [items]);

  async function handleFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    setUploading(true);
    setMessage("");
    try {
      const results = await Promise.allSettled(
        files.map((file) => uploadImage(file, uploadFolder, { token })),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (failed === 0) flash(`${ok} image${ok === 1 ? "" : "s"} uploaded.`);
      else if (ok === 0) setMessage(`${failed} upload${failed === 1 ? "" : "s"} failed. Please try again.`);
      else flash(`${ok} uploaded, ${failed} failed.`);
      setReloadKey((k) => k + 1);
    } finally {
      setUploading(false);
    }
  }

  function handleInput(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    event.target.value = "";
    if (files) void handleFiles(files);
  }

  function onDragEnter(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (uploading) return;
    dragDepth.current += 1;
    setDragging(true);
  }
  function onDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
  }
  function onDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }
  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (uploading) return;
    if (event.dataTransfer?.files?.length) void handleFiles(event.dataTransfer.files);
  }

  function toggleSelect(url: string) {
    setSelected((prev) => {
      if (multiple) {
        if (prev.includes(url)) return prev.filter((u) => u !== url);
        if (prev.length >= maxSelect) return prev;
        return [...prev, url];
      }
      return prev[0] === url ? [] : [url];
    });
  }

  function handleCardClick(item: MediaItem) {
    if (pickMode) {
      toggleSelect(item.url);
      return;
    }
    if (item.type === "image") setPreview(item);
  }

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      flash("URL copied to clipboard.");
    } catch {
      flash("Couldn't copy automatically — open the file and copy from the address bar.");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyName(deleteTarget.name);
    try {
      await deleteMedia(token, scope, deleteTarget.folder, deleteTarget.name);
      setDeleteTarget(null);
      flash("File deleted.");
      setReloadKey((k) => k + 1);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Could not delete the file. Please try again.");
    } finally {
      setBusyName(null);
    }
  }

  const countLabel = loading ? "Loading…" : `${filtered.length} of ${items.length} files`;
  const inputClass =
    "h-10 w-full rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[180px] flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files…"
            className={`${inputClass} pl-9`}
          />
        </div>

        <select
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          className={`${inputClass} max-w-[200px] cursor-pointer`}
        >
          {MEDIA_FOLDERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
              {f.value && counts.get(f.value) ? ` (${counts.get(f.value)})` : ""}
            </option>
          ))}
        </select>

        {allowUpload && (
          <button
            type="button"
            onClick={() => !uploading && inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={`${uploading ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} text-sm`} />
            </span>
            {uploading ? "Uploading…" : "Upload"}
          </button>
        )}

        <span className="ml-auto text-xs text-foreground-500">{countLabel}</span>
      </div>

      {/* Dropzone */}
      {allowUpload && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => !uploading && inputRef.current?.click()}
          onKeyDown={(event) => {
            if ((event.key === "Enter" || event.key === " ") && !uploading) inputRef.current?.click();
          }}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`mb-4 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed px-4 py-5 text-center transition-colors ${
            dragging
              ? "border-primary-400 bg-primary-50"
              : "border-background-300 bg-background-50 hover:border-primary-300 hover:bg-background-100"
          } ${uploading ? "cursor-wait opacity-70" : ""}`}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-primary-600">
            <i className={uploading ? "ri-loader-4-line animate-spin text-lg" : "ri-drag-drop-line text-lg"} />
          </span>
          <p className="text-xs font-semibold text-foreground-800">
            {uploading ? "Uploading…" : "Drag & drop images here"}
          </p>
          <p className="text-[11px] text-foreground-500">PNG, JPG or WebP — or click to browse.</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        onChange={handleInput}
        className="hidden"
        disabled={uploading}
      />

      {message && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-4 py-2.5 text-xs font-semibold text-foreground-700">
          <i className="ri-information-line text-sm" />
          {message}
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <span className="flex items-start gap-2.5">
            <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
            {error}
          </span>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-background-200 py-16 text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-image-2-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-medium text-foreground-700">
            {items.length === 0 ? "No media files yet" : "No files match your filter"}
          </p>
          <p className="mt-1 text-xs text-foreground-500">
            {items.length === 0
              ? "Upload an image to start building your media library."
              : "Try a different search or folder."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          {filtered.map((item) => {
            const isSelected = pickMode && selected.includes(item.url);
            return (
              <div
                key={`${item.folder}/${item.name}`}
                className={`group overflow-hidden rounded-xl border bg-background-50 transition-colors ${
                  isSelected ? "border-primary-500 ring-2 ring-primary-200" : "border-background-200"
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleCardClick(item)}
                  className="relative block h-28 w-full cursor-pointer bg-background-100"
                  title={pickMode ? (isSelected ? "Selected" : "Select this image") : "Preview"}
                >
                  {item.type === "image" ? (
                    <img src={item.url} alt={item.name} className="h-28 w-full object-cover" />
                  ) : (
                    <span className="flex h-28 w-full items-center justify-center text-foreground-400">
                      <i className={`${item.type === "font" ? "ri-font-size-2" : "ri-file-line"} text-3xl`} />
                    </span>
                  )}
                  <span className="absolute left-2 top-2 rounded-full bg-foreground-950/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-background-50">
                    {item.folder}
                  </span>
                  {isSelected && (
                    <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-check-line text-xs" />
                    </span>
                  )}
                </button>

                <div className="p-2.5">
                  <p className="truncate text-[11px] font-semibold text-foreground-800" title={item.name}>
                    {item.name}
                  </p>
                  <p className="mt-0.5 text-[10px] text-foreground-500">{formatBytes(item.size)}</p>

                  {!pickMode && (
                    <div className="mt-2 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => copyUrl(item.url)}
                        title="Copy URL"
                        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
                      >
                        <i className="ri-file-copy-line text-sm" />
                      </button>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        title="Open in new tab"
                        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
                      >
                        <i className="ri-external-link-line text-sm" />
                      </a>
                      {allowDelete && (scope === "admin" || item.owned === true) && (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          title="Delete file"
                          className="ml-auto flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-rose-600 transition-colors hover:bg-rose-50"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pick-mode action bar */}
      {pickMode && (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
          <span className="text-xs text-foreground-600">
            {selected.length === 0
              ? "Select an image to continue"
              : `${selected.length} image${selected.length === 1 ? "" : "s"} selected`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSelect?.(selected)}
              disabled={selected.length === 0}
              className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-check-line text-sm" /> Use selected
            </button>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-3.5">
              <p className="truncate text-sm font-semibold text-foreground-900" title={preview.name}>
                {preview.name}
              </p>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              <img
                src={preview.url}
                alt={preview.name}
                className="mx-auto max-h-[55vh] w-auto rounded-lg border border-background-200 object-contain"
              />
              <p className="mt-3 break-all text-center text-[11px] text-foreground-500">{preview.url}</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-background-200 px-5 py-3.5">
              <button
                type="button"
                onClick={() => copyUrl(preview.url)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-file-copy-line text-sm" /> Copy URL
              </button>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-xl border border-background-200 bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-background-200 px-5 py-4">
              <h3 className="font-heading text-base font-bold text-foreground-950">Delete file</h3>
              <p className="mt-0.5 truncate text-xs text-foreground-500">{deleteTarget.name}</p>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-foreground-700">
                Delete <strong>{deleteTarget.name}</strong> from the <strong>{deleteTarget.folder}</strong>{" "}
                folder? This permanently removes the file from the server.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-background-200 px-5 py-4">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busyName === deleteTarget.name}
                onClick={confirmDelete}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className="ri-delete-bin-line text-sm" /> Delete file
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}